"use client";

import {
  useState,
  useCallback,
  useEffect,
  useRef,
  lazy,
  Suspense,
} from "react";
import { useRouter } from "next/navigation";
import {
  invoiceSchema,
  PDF_DATA_LOCAL_STORAGE_KEY,
  type InvoiceData,
} from "@/app/schema";
import { getInitialInvoiceData } from "@/app/constants";
import { useInvoiceForm } from "../components/invoice-form/hooks/use-invoice-form";
import { DeferredTooltipProvider } from "@/components/ui/deferred-tooltip-provider";
import { toast } from "sonner";
import { handleInvoiceNumberBreakingChange } from "../utils/invoice-number-breaking-change";
import { useDeviceContext } from "@/contexts/device-context";
import { useEncryptionPassword } from "@/contexts/encryption-password-context";
import { EncryptionPasswordDialog } from "@/components/password/encryption-password-dialog";
import { saveReceipt, areReceiptsEncrypted } from "../utils/saved-receipts";
import {
  ReceiptWizard,
  type InvoicePDFDownloadLinkRef,
} from "../components/pdf-components";
import { STORAGE_KEYS } from "@/lib/storage-keys";

// Lazy load the PDF download component - only loaded when needed
const InvoicePDFDownloadLink = lazy(() =>
  import("../components/invoice-pdf-download-link").then((mod) => ({
    default: mod.InvoicePDFDownloadLink,
  })),
);

/**
 * Create Receipt Page - Standalone page for the receipt wizard
 * This page allows users to create a new receipt using the step-by-step form
 *
 * PERFORMANCE OPTIMIZATIONS:
 * 1. Preloads ReceiptWizard chunk immediately on mount (not waiting for idle)
 * 2. Uses requestIdleCallback for non-critical PDF chunk preloading
 * 3. Optimistic data loading with fallback state
 */
export function CreateReceiptPageClient() {
  const router = useRouter();
  const { isDesktop } = useDeviceContext();
  const { password, hasPassword, setPassword, hasBeenPrompted, markPrompted } =
    useEncryptionPassword();
  const isMobile = !isDesktop;

  // Password dialog state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordDialogMode, setPasswordDialogMode] = useState<
    "create" | "enter"
  >("create");
  const [pendingSave, setPendingSave] = useState(false);

  // CRITICAL: Preload ReceiptWizard chunk immediately on mount
  // This ensures the wizard is available as soon as data is ready
  useEffect(() => {
    // Preload ReceiptWizard chunk immediately - this is critical for fast page load
    void import("../components/receipt-wizard/ReceiptWizard");
  }, []);

  // Preload PDF chunk after initial render to speed up download
  useEffect(() => {
    // Use requestIdleCallback or setTimeout to preload after critical rendering
    const preloadPDF = () => {
      // Preload the InvoicePDFDownloadLink component chunk
      void import("../components/invoice-pdf-download-link");
    };

    // Wait for the page to be fully interactive before preloading
    if (typeof window !== "undefined") {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(preloadPDF, { timeout: 2000 });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(preloadPDF, 2000);
      }
    }
  }, []);

  // Initialize with default data immediately for instant render
  // Data is updated in the background if saved data exists
  const [invoiceDataState, setInvoiceDataState] = useState<InvoiceData>(
    getInitialInvoiceData(),
  );
  const [, setIsDataLoading] = useState(true);
  const [, setInvoiceFormHasErrors] = useState(false);
  const [documentType, setDocumentType] = useState<"original" | "copy">(
    "original",
  );
  const pdfDownloadRef = useRef<InvoicePDFDownloadLinkRef>(null);
  const [errorWhileGeneratingPdfIsShown, setErrorWhileGeneratingPdfIsShown] =
    useState(false);
  const [showPDFComponent, setShowPDFComponent] = useState(false);

  // Load saved data from localStorage in the background
  // This is non-blocking - form renders immediately with defaults
  useEffect(() => {
    // Use setTimeout to defer localStorage read until after initial paint
    const loadSavedData = () => {
      try {
        const savedData = localStorage.getItem(PDF_DATA_LOCAL_STORAGE_KEY);
        if (savedData) {
          const json: unknown = JSON.parse(savedData);

          // Migration and validation
          if (typeof json === "object" && json !== null) {
            const data = json as Record<string, unknown>;

            // Clear old default values
            if (data.seller && typeof data.seller === "object") {
              const seller = data.seller as Record<string, unknown>;
              if (seller.vatNo === "000000000") seller.vatNo = "";
            }
            if (data.buyer && typeof data.buyer === "object") {
              const buyer = data.buyer as Record<string, unknown>;
              if (buyer.vatNo === "000000000") buyer.vatNo = "";
            }
          }

          const updatedJson = handleInvoiceNumberBreakingChange(json);
          const parseResult = invoiceSchema.safeParse(updatedJson);

          if (parseResult.success) {
            setInvoiceDataState(parseResult.data);
          } else {
            // Invalid saved data - clear it and use defaults
            localStorage.removeItem(PDF_DATA_LOCAL_STORAGE_KEY);
            toast.error("נתוני הקבלה השמורים לא תקינים. הטופס אופס.", {
              duration: 3000,
            });
          }
        }
      } catch (error) {
        console.error("Failed to load saved invoice data:", error);
        localStorage.removeItem(PDF_DATA_LOCAL_STORAGE_KEY);
        toast.error("לא ניתן לטעון את נתוני הקבלה השמורים. איפסנו את הטופס.", {
          duration: 5000,
        });
      } finally {
        setIsDataLoading(false);
      }
    };

    // Defer loading to allow initial render to complete
    if (typeof window !== "undefined") {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(loadSavedData, { timeout: 100 });
      } else {
        setTimeout(loadSavedData, 0);
      }
    }
  }, []);

  // Handle form data changes
  const handleInvoiceDataChange = useCallback((updatedData: InvoiceData) => {
    setInvoiceDataState(updatedData);
  }, []);

  // Use the invoice form hook
  // Form is ready immediately with initial data, updated when saved data loads
  const { form } = useInvoiceForm({
    invoiceData: invoiceDataState,
    onSubmit: handleInvoiceDataChange,
    isMobile,
    setInvoiceFormHasErrors,
  });

  // Handle password submission from dialog
  const handlePasswordSubmit = useCallback(
    async (submittedPassword: string) => {
      setPassword(submittedPassword);
      setShowPasswordDialog(false);

      // If there was a pending save, complete it now
      if (pendingSave && invoiceDataState) {
        setPendingSave(false);
        try {
          await saveReceipt(invoiceDataState, submittedPassword);
          toast.success("הקבלה נשמרה בהצלחה! מעביר אותך לרשימת הקבלות...", {
            duration: 2000,
          });
          setTimeout(() => {
            router.push("/");
          }, 1500);
        } catch (error) {
          toast.error("שגיאה בשמירת הקבלה", {
            description: error instanceof Error ? error.message : "נסה שוב",
            duration: 3000,
          });
        }
      }
    },
    [setPassword, pendingSave, invoiceDataState, router],
  );

  // Handle completion - save receipt and redirect to home
  const handleComplete = useCallback(
    async (_documentType?: "original" | "copy") => {
      if (!invoiceDataState) return;

      // Check if we need a password for encryption
      // CRITICAL: Password is MANDATORY for legal compliance - receipts must be encrypted
      if (!hasPassword || !password || password.length === 0) {
        // ALWAYS show password dialog if no password is set
        // Password is mandatory for encrypting receipts
        const hasEncryptedReceipts = areReceiptsEncrypted();

        // Show password dialog - either "enter" mode if receipts exist, "create" mode for new users
        setPasswordDialogMode(hasEncryptedReceipts ? "enter" : "create");
        setShowPasswordDialog(true);
        setPendingSave(true);
        markPrompted(); // Mark that user has been prompted for password
        return;
      }

      try {
        // MANDATORY ENFORCEMENT: Password must be a valid non-empty string
        // This ensures receipts are ALWAYS encrypted as required by law
        if (!password || password.length === 0) {
          toast.error("נדרשת סיסמה לשמירת הקבלה", {
            description: "אנא הגדר סיסמה להצפנת הקבלה",
            duration: 3000,
          });
          setPasswordDialogMode(areReceiptsEncrypted() ? "enter" : "create");
          setShowPasswordDialog(true);
          setPendingSave(true);
          return;
        }

        // Save the receipt with password (mandatory encryption)
        await saveReceipt(invoiceDataState, password);

        toast.success("הקבלה נשמרה בהצלחה! מעביר אותך לרשימת הקבלות...", {
          duration: 2000,
        });

        // Navigate back to home where receipts grid is displayed
        setTimeout(() => {
          router.push("/");
        }, 1500);
      } catch (error) {
        toast.error("שגיאה בשמירת הקבלה", {
          description: error instanceof Error ? error.message : "נסה שוב",
          duration: 3000,
        });
      }
    },
    [
      router,
      invoiceDataState,
      hasPassword,
      password,
      hasBeenPrompted,
      markPrompted,
    ],
  );

  // Handle download with document type
  const handleDownload = useCallback((type: "original" | "copy") => {
    setDocumentType(type);

    // Store document type in localStorage for the home page to use
    localStorage.setItem(STORAGE_KEYS.DOCUMENT_TYPE, type);

    // Show the PDF component (triggers lazy loading)
    setShowPDFComponent(true);

    // Actually trigger the PDF download (will be called after component mounts)
    // Use a small delay to allow the lazy-loaded component to mount
    setTimeout(() => {
      if (pdfDownloadRef.current) {
        pdfDownloadRef.current.downloadPDF(type);
      } else {
        toast.error("שגיאה בהורדת הקובץ", {
          description: "אנא נסה שוב",
        });
      }
    }, 100);
  }, []);

  return (
    <DeferredTooltipProvider delayDuration={0}>
      <div
        className="relative flex h-dvh flex-col overflow-hidden overscroll-none bg-white"
        dir="rtl"
        style={{
          touchAction: "none",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        {/* Onboarding Form */}
        <main className="flex-1 overflow-hidden">
          <ReceiptWizard
            form={form}
            invoiceData={invoiceDataState}
            onComplete={handleComplete}
            onDownload={handleDownload}
            documentType={documentType}
          />
        </main>

        {/* Lazy-loaded PDF download component - only rendered when needed */}
        {showPDFComponent && (
          <Suspense fallback={null}>
            <InvoicePDFDownloadLink
              ref={pdfDownloadRef}
              invoiceData={invoiceDataState}
              errorWhileGeneratingPdfIsShown={errorWhileGeneratingPdfIsShown}
              setErrorWhileGeneratingPdfIsShown={
                setErrorWhileGeneratingPdfIsShown
              }
              isMobile={isMobile}
              documentType={documentType}
              businessType={
                (invoiceDataState?.businessType as
                  | "osek-patur"
                  | "eseq-zair") || "osek-patur"
              }
            />
          </Suspense>
        )}

        {/* Password Dialog for Encryption */}
        <EncryptionPasswordDialog
          isOpen={showPasswordDialog}
          onClose={() => {
            setShowPasswordDialog(false);
            setPendingSave(false);
          }}
          onSubmit={handlePasswordSubmit}
          mode={passwordDialogMode}
        />
      </div>
    </DeferredTooltipProvider>
  );
}
